import { SeoHead } from "@/components/SeoHead";
import { Sparkles, ShieldCheck, BookOpen, Brain, Heart } from "lucide-react";

export default function FairUsage() {
  return (
    <div data-testid="fair-usage-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <SeoHead title="Fair Usage · SugarSense" description="How SugarSense advisor capacity works across plans." />

      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Fair Usage</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-2 leading-tight">
        Advisor capacity, <span className="italic">explained quietly.</span>
      </h1>
      <p className="mt-4 text-cocoa/75 text-base leading-relaxed max-w-2xl">
        SugarSense isn't a chat bot — it's a private advisor that learns the pattern of your relationship over time. Deeper reads, richer memory, and longer pattern reports use more of our intelligence engine, so we tier them across plans. Here's how it works.
      </p>

      <section className="mt-10 space-y-5">
        <Block icon={Sparkles} title="What counts as an AI Insight">
          One advisor read, one strategy plan, one message draft, or one pattern report. Short, focused requests use less of your monthly capacity. Deeper, longer analyses use more. We never count administrative actions, browsing your saved data, or reviewing past summaries.
        </Block>
        <Block icon={Brain} title="What changes between plans">
          Higher plans don't just include more insights — they include <span className="italic">deeper</span> intelligence. Deep memory continuity, longitudinal trajectory reports, and priority AI processing become available as you move up. The pattern recognition becomes more longitudinal, not just more frequent.
        </Block>
        <Block icon={Heart} title="What never gets locked">
          Your saved profiles, timeline, gift tracker, drafts, and historical reports remain fully accessible at every plan tier — even after you've used your monthly capacity. Capacity is a forward-looking gate on new AI work, never a wall around your own data.
        </Block>
        <Block icon={ShieldCheck} title="Safety always comes first">
          If you describe a situation involving immediate safety, abuse, threats, or self-harm, SugarSense will always provide a focused safety response — regardless of your plan or remaining capacity.
        </Block>
        <Block icon={BookOpen} title="Long inputs">
          To keep things efficient on lighter plans, very long submissions may be trimmed to focus on the most recent and most framing material. Higher plans accept longer inputs without trimming.
        </Block>
      </section>

      <div className="mt-12 ss-card-premium p-6 text-center">
        <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Our promise</p>
        <p className="font-heading italic text-xl text-cocoa mt-2 leading-snug">
          We charge for depth, not for access to your own story.
        </p>
        <p className="text-[12px] text-cocoa/60 mt-3">
          Capacity refreshes monthly. You can change plans or cancel from your <a href="/billing" className="underline hover:text-cocoa">Billing</a> page at any time.
        </p>
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <div className="ss-card p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-champagne/60 flex items-center justify-center">
          <Icon size={16} className="text-mauve" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg text-cocoa leading-tight">{title}</h2>
          <p className="mt-2 text-[14px] text-cocoa/75 leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  );
}
